import type { Lerpable } from '../types';

export const copy = <T extends Lerpable>(
  target: T,
  v: T,
): T => {
  const vs = v as number[];
  const vvs = v as number[][];

  if (typeof v === 'number') return v as T;
  else if (typeof vs[0] === 'number') {
    const t = target as number[];
    const n = t.length;
    for (let i = 0; i < n; ++i) t[i] = vs[i];
    return target;
  }
  else if (typeof vvs[0][0] === 'number') {
    const t = target as number[][];
    const n = t.length;
    for (let i = 0; i < n; ++i) {
      const vv = vvs[i];
      const tt = t[i];
      const m = tt.length;
      for (let j = 0; j < m; ++j) {
        tt[j] = vv[j];
      }
    }
    return target;
  }
  else {
    throw new Error(`Cannot copy value for '${v}'`);
  }
};

export const clone = (v: Lerpable) => {
  const vvs = v as number[][];
  const vs = v as number[];
  if (typeof v === 'number') return v;
  if (typeof vs[0] === 'number') return new Float32Array([...vs]);
  if (typeof vvs[0]?.[0] === 'number') return vvs.map(vs => new Float32Array([...vs]));
  return JSON.parse(JSON.stringify(v));
};

export const for2 = <T extends Lerpable>(
  a: T,
  b: T,
  fn: (a: number, b: number) => void,
) => {
  const as = a as number[];
  const bs = b as number[];
  const aas = a as number[][];
  const bbs = b as number[][];

  if (typeof a === 'number') fn(a as number, b as number);
  else if (typeof as[0] === 'number') {
    const n = as.length;
    for (let i = 0; i < n; ++i) fn(as[i], bs[i]);
  }
  else if (typeof aas[0][0] === 'number') {
    const n = aas.length;
    for (let i = 0; i < n; ++i) {
      const aa = aas[i];
      const bb = bbs[i];
      const m = aa.length;
      for (let j = 0; j < m; ++j) {
        fn(aa[j], bb[j]);
      }
    }
  }
  else {
    throw new Error(`Cannot for2 value for '${a}' '${b}'`);
  }
};

export const zip2 = <T extends Lerpable>(
  target: T,
  a: T,
  b: T,
  fn: (a: number, b: number) => number,
): T => {
  const as = a as number[];
  const bs = b as number[];
  const aas = a as number[][];
  const bbs = b as number[][];

  if (typeof a === 'number') return fn(a as number, b as number) as T;
  else if (typeof as[0] === 'number') {
    const t = target as number[];
    const n = t.length;
    for (let i = 0; i < n; ++i) t[i] = fn(as[i], bs[i]);
    return target;
  }
  else if (typeof aas[0][0] === 'number') {
    const t = target as number[][];
    const n = t.length;
    for (let i = 0; i < n; ++i) {
      const aa = aas[i];
      const bb = bbs[i];
      const tt = t[i];
      const m = tt.length;
      for (let j = 0; j < m; ++j) {
        tt[j] = fn(aa[j], bb[j]);
      }
    }
    return target;
  }
  else {
    throw new Error(`Cannot zip2 value for '${a}' '${b}'`);
  }
};

export const for4 = <T extends Lerpable>(
  a: T,
  b: T,
  c: T,
  d: T,
  fn: (a: number, b: number, c: number, d: number) => void,
) => {
  const as = a as number[];
  const bs = b as number[];
  const cs = c as number[];
  const ds = d as number[];
  const aas = a as number[][];
  const bbs = b as number[][];
  const ccs = c as number[][];
  const dds = d as number[][];

  if (typeof a === 'number') fn(a as number, b as number, c as number, d as number);
  else if (typeof as[0] === 'number') {
    const n = as.length;
    for (let i = 0; i < n; ++i) fn(as[i], bs[i], cs[i], ds[i]);
  }
  else if (typeof aas[0][0] === 'number') {
    const n = aas.length;
    for (let i = 0; i < n; ++i) {
      const aa = aas[i];
      const bb = bbs[i];
      const cc = ccs[i];
      const dd = dds[i];
      const m = aa.length;
      for (let j = 0; j < m; ++j) {
        fn(aa[j], bb[j], cc[j], dd[j]);
      }
    }
  }
  else {
    throw new Error(`Cannot zip4 value for '${a}' '${b}' '${c}' '${d}'`);
  }
};

export const zip4 = <T extends Lerpable>(
  target: T,
  a: T,
  b: T,
  c: T,
  d: T,
  fn: (a: number, b: number, c: number, d: number) => number,
): T => {
  const as = a as number[];
  const bs = b as number[];
  const cs = c as number[];
  const ds = d as number[];
  const aas = a as number[][];
  const bbs = b as number[][];
  const ccs = c as number[][];
  const dds = d as number[][];

  if (typeof a === 'number') return fn(a as number, b as number, c as number, d as number) as T;
  else if (typeof as[0] === 'number') {
    const t = target as number[];
    const n = t.length;
    for (let i = 0; i < n; ++i) t[i] = fn(as[i], bs[i], cs[i], ds[i]);
    return target;
  }
  else if (typeof aas[0][0] === 'number') {
    const t = target as number[][];
    const n = t.length;
    for (let i = 0; i < n; ++i) {
      const aa = aas[i];
      const bb = bbs[i];
      const cc = ccs[i];
      const dd = dds[i];
      const tt = t[i];
      const m = tt.length;
      for (let j = 0; j < m; ++j) {
        tt[j] = fn(aa[j], bb[j], cc[j], dd[j]);
      }
    }
    return target;
  }
  else {
    throw new Error(`Cannot zip4 value for '${a}' '${b}' '${c}' '${d}'`);
  }
};
